import React, { useEffect, useState } from "react";
import { db, auth } from "./firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  runTransaction,
  serverTimestamp
} from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import "./navbar-spacing.css";
import "./Dashboard.css";
import MenuEditorOverlay from "./MenuEditorOverlay";

// Helper stili per admin
const statCardStyle = (bg) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "0.6rem 1.1rem",
  background: bg,
  color: "white",
  borderRadius: "10px",
  minWidth: "90px",
  gap: "2px"
});

const selectStyle = {
  padding: "0.45rem 0.75rem",
  borderRadius: "8px",
  border: "1px solid #c5cfe8",
  background: "white",
  fontSize: "0.88rem",
  cursor: "pointer"
};

function OrdersDashboard() {
  const [ordini, setOrdini] = useState([]);
  const [menu, setMenu] = useState([]);
  const [localeTag, setLocaleTag] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [ordineSelezionato, setOrdineSelezionato] = useState(null);
  const [snackbar, setSnackbar] = useState("");
  const [ordineInEliminazione, setOrdineInEliminazione] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState("oggi");

/* Filtri mostrati quando viene selezionato "Tutti" */
const [filtroTipoOrdine, setFiltroTipoOrdine] = useState("tutti");
const [filtroStatoOrdine, setFiltroStatoOrdine] = useState("tutti");
const [filtroPeriodoTutti, setFiltroPeriodoTutti] = useState("tutti");
const [dataDa, setDataDa] = useState("");
const [dataA, setDataA] = useState("");

const [camerieriMap, setCamerieriMap] = useState({});

  // Filtri admin
  const [tuttiLocali, setTuttiLocali] = useState([]);
  const [filtroLocale, setFiltroLocale] = useState("tutti");
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [filtroRicerca, setFiltroRicerca] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tavoloFiltro = searchParams.get("tavolo");

  useEffect(() => {
    const fetchCamerieri = async () => {
      try {
        const snapshot = await getDocs(collection(db, "camerieri"));
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          map[docSnap.id] = docSnap.data().nome;
        });
        setCamerieriMap(map);
      } catch (error) {
        console.error("Errore caricamento camerieri:", error);
      }
    };
    fetchCamerieri();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const utenteRef = doc(db, "utenti", user.uid);
        const utenteSnap = await getDoc(utenteRef);
        const datiUtente = utenteSnap.data() || {};
        const ruolo = datiUtente.ruolo || "";
        const tag = datiUtente.localeTag || "";

        // 🔴 BLOCCO ORDINI (solo per gestore)
        if (ruolo !== "admin") {
          const localeRef = doc(db, "locali", tag);
          const localeSnap = await getDoc(localeRef);

          if (localeSnap.exists()) {
            const data = localeSnap.data();

            if (data.nascondiOrdini) {
              navigate("/");
              return;
            }
          }
        }

        if (ruolo === "admin") {
          setIsAdmin(true);

          const localiSnap = await getDocs(collection(db, "locali"));
          setTuttiLocali(localiSnap.docs.map((d) => d.id));
        } else {
          setLocaleTag(tag);
        }
      } catch (error) {
        console.error("Errore lettura utente:", error);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!localeTag) return;

    const fetchMenu = async () => {
      try {
        const snapshot = await getDocs(collection(db, "menu"));
        const lista = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter(
            (item) =>
              item.localeTag?.trim().toLowerCase() ===
              localeTag.trim().toLowerCase()
          );

        const menuFiltrato = lista.map((item) => ({
          id: item.id,
          name: item.nome || "Senza nome",
          price: parseFloat(item.prezzo) || 0,
          categoria: item.categoria || ""
        }));

        setMenu(menuFiltrato);
      } catch (error) {
        console.error("Errore caricamento menu:", error);
      }
    };

    fetchMenu();
  }, [localeTag]);

  const getCategoriaByName = (dishName) => {
    const found = menu.find((m) => m.name === dishName);
    return found?.categoria || "";
  };

  const getCategoryHue = (categoria) => {
    if (!categoria) return 200;
    let hash = 0;
    for (let i = 0; i < categoria.length; i++) {
      hash = categoria.charCodeAt(i) + ((hash << 5) - hash);
      hash |= 0;
    }
    return Math.abs(hash) % 360;
  };

  const ordineHaStato = (ordine, statoRichiesto) => {
  if (statoRichiesto === "tutti") return true;

  const items = ordine.items || ordine.piatti || [];

  if (items.length === 0) {
    return (ordine.stato || "inviato") === statoRichiesto;
  }

  return items.some((item, index) => {
    const statoItem =
      item.stato ||
      ordine.itemStati?.[String(index)] ||
      ordine.stato ||
      "inviato";

    return statoItem === statoRichiesto;
  });
};

  useEffect(() => {
    // Admin: carica senza filtro locale; utente normale: aspetta localeTag
    if (!isAdmin && !localeTag) return;

    setLoading(true);

    // Admin vede tutto, utente normale solo il suo locale
    const q = isAdmin
      ? query(collection(db, "ordini"))
      : query(collection(db, "ordini"), where("locale", "==", localeTag));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const inizioGiornata = new Date();
        inizioGiornata.setHours(0, 0, 0, 0);
        const fineGiornata = new Date(inizioGiornata);
        fineGiornata.setDate(fineGiornata.getDate() + 1);

        let lista = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => {
            // Admin: ordina per locale poi tavolo
            if (isAdmin) {
              const localeA = (a.locale || "").localeCompare(b.locale || "");
              if (localeA !== 0) return localeA;
            }
            return parseInt(a.table || 0, 10) - parseInt(b.table || 0, 10);
          });

        if (tavoloFiltro) {
          lista = lista.filter(
            (o) =>
              String(o.table).toUpperCase() === String(tavoloFiltro).toUpperCase()
          );
        }

 if (filtroPeriodo === "oggi") {
  lista = lista.filter((o) => {
    const ts = o.timestamp?.seconds
      ? new Date(o.timestamp.seconds * 1000)
      : null;

    if (!ts) return false;

    return ts >= inizioGiornata && ts < fineGiornata;
  });
}

if (filtroPeriodo === "tutti") {
  /* FILTRO SALA / ASPORTO */
  if (filtroTipoOrdine !== "tutti") {
    lista = lista.filter((o) => {
      const tavolo = String(o.table || "").toUpperCase();
      const isAsporto = tavolo.startsWith("ASPORTO");

      if (filtroTipoOrdine === "asporto") {
        return isAsporto;
      }

      if (filtroTipoOrdine === "sala") {
        return !isAsporto;
      }

      return true;
    });
  }

  /* FILTRO STATO */
  if (filtroStatoOrdine !== "tutti") {
    lista = lista.filter((o) =>
      ordineHaStato(o, filtroStatoOrdine)
    );
  }

  /* FILTRO PERIODO */
  if (filtroPeriodoTutti === "settimana") {
    const oggi = new Date();

    const inizioSettimana = new Date(oggi);
    const giornoSettimana = inizioSettimana.getDay();
    const giorniDaSottrarre =
      giornoSettimana === 0 ? 6 : giornoSettimana - 1;

    inizioSettimana.setDate(
      inizioSettimana.getDate() - giorniDaSottrarre
    );
    inizioSettimana.setHours(0, 0, 0, 0);

    lista = lista.filter((o) => {
      const ts = o.timestamp?.seconds
        ? new Date(o.timestamp.seconds * 1000)
        : null;

      return ts && ts >= inizioSettimana;
    });
  }

  if (filtroPeriodoTutti === "mese") {
    const oggi = new Date();

    const inizioMese = new Date(
      oggi.getFullYear(),
      oggi.getMonth(),
      1
    );

    inizioMese.setHours(0, 0, 0, 0);

    lista = lista.filter((o) => {
      const ts = o.timestamp?.seconds
        ? new Date(o.timestamp.seconds * 1000)
        : null;

      return ts && ts >= inizioMese;
    });
  }

  if (
    filtroPeriodoTutti === "personalizzato" &&
    (dataDa || dataA)
  ) {
    const inizio = dataDa
      ? new Date(`${dataDa}T00:00:00`)
      : null;

    const fine = dataA
      ? new Date(`${dataA}T23:59:59.999`)
      : null;

    lista = lista.filter((o) => {
      const ts = o.timestamp?.seconds
        ? new Date(o.timestamp.seconds * 1000)
        : null;

      if (!ts) return false;
      if (inizio && ts < inizio) return false;
      if (fine && ts > fine) return false;

      return true;
    });
  }
}

// 🔥 NON raggruppiamo più qui
setOrdini(lista);
        setLoading(false);
      },
      (err) => {
        console.error("Errore caricamento ordini:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
  localeTag,
  isAdmin,
  tavoloFiltro,
  filtroPeriodo,
  filtroTipoOrdine,
  filtroStatoOrdine,
  filtroPeriodoTutti,
  dataDa,
  dataA
]);

  const liberaSlotAsporto = async (ordineData) => {
  if (
    !ordineData?.slotAsporto ||
    !ordineData?.slotData ||
    !ordineData?.slotQuantita ||
    !ordineData?.locale
  ) {
    return;
  }

  const slotDocRef = doc(
    db,
    "asporto_slot_occupazione",
    `${ordineData.locale}_${ordineData.slotData}`
  );

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(slotDocRef);
    if (!snap.exists()) return;

    const data = snap.data();
    const slots = data.slots || {};
    const slotId = String(ordineData.slotAsporto).replace(":", "-");

    const occupati = Number(slots[slotId] || 0);
    const daLiberare = Number(ordineData.slotQuantita || 0);

    transaction.update(slotDocRef, {
      slots: {
        ...slots,
        [slotId]: Math.max(0, occupati - daLiberare)
      },
      updatedAt: serverTimestamp()
    });
  });
};

  const eliminaOrdine = async (ordineId) => {
    try {
      const gruppo = ordineInEliminazione;

      let timestampsToDelete = [];

      if (gruppo && Array.isArray(gruppo.ordiniIds)) {
        for (const id of gruppo.ordiniIds) {
          const docSnap = await getDoc(doc(db, "ordini", id));
          if (docSnap.exists()) {
            timestampsToDelete.push(docSnap.data().timestamp?.seconds);
          }
        }
      } else {
        const docSnap = await getDoc(doc(db, "ordini", ordineId));
        if (docSnap.exists()) {
          timestampsToDelete.push(docSnap.data().timestamp?.seconds);
        }
      }


      const tableNumber = gruppo?.table || ordini.find((o) => o.id === ordineId)?.table;
      const localeOrdineDaUsare = gruppo?.locale || localeTag;

      if (tableNumber && localeOrdineDaUsare && timestampsToDelete.length > 0) {
        const normalized = String(localeOrdineDaUsare)
          .toLowerCase()
          .replace(/[^a-z0-9]/gi, "");

        const sentColPath = `ordiniTemp/${normalized}_${tableNumber}/sent`;

        try {
          const sentSnapshot = await getDocs(collection(db, sentColPath));
          const toDelete = sentSnapshot.docs.filter((sDoc) =>
            timestampsToDelete.includes(sDoc.data().timestamp?.seconds)
          );

          await Promise.all(
            toDelete.map((sDoc) => deleteDoc(doc(db, sentColPath, sDoc.id)))
          );
        } catch (e) {
          console.debug(
            "Pulizia ordiniTemp/sent non riuscita:",
            e?.message || e
          );
        }
      }

     if (gruppo && Array.isArray(gruppo.ordiniIds)) {
  for (const id of gruppo.ordiniIds) {
    const ordineSnap = await getDoc(doc(db, "ordini", id));

    if (ordineSnap.exists()) {
      await liberaSlotAsporto(ordineSnap.data());
    }

    await deleteDoc(doc(db, "ordini", id));
  }
} else {
  const ordineSnap = await getDoc(doc(db, "ordini", ordineId));

  if (ordineSnap.exists()) {
    await liberaSlotAsporto(ordineSnap.data());
  }

  await deleteDoc(doc(db, "ordini", ordineId));
}

      setOrdineInEliminazione(null);
      setSnackbar("✅ Ordine eliminato!");
      setTimeout(() => setSnackbar(""), 3000);
    } catch (err) {
      console.error("Errore eliminazione:", err);
      setSnackbar("❌ Errore eliminazione ordine");
      setTimeout(() => setSnackbar(""), 3000);
    }
  };


  // Filtro client-side per admin (locale, stato, ricerca)
  const ordiniFiltrati = isAdmin
    ? ordini.filter((o) => {
        if (filtroLocale !== "tutti" && o.locale !== filtroLocale) return false;
        if (filtroStato !== "tutti" && (o.stato || "in attesa") !== filtroStato) return false;
        if (filtroRicerca) {
          const q = filtroRicerca.toLowerCase();
          const matchTable = String(o.table || "").toLowerCase().includes(q);
          const matchLocale = (o.locale || "").toLowerCase().includes(q);
          const matchItem = (o.items || []).some((i) =>
            i.name.toLowerCase().includes(q)
          );
          if (!matchTable && !matchLocale && !matchItem) return false;
        }
        return true;
      })
    : ordini;

  // Statistiche admin
  const statsAdmin = isAdmin
    ? {
        totaleOrdini: ordiniFiltrati.length,
        totaleIncasso: ordiniFiltrati.reduce(
          (sum, o) =>
            sum +
            (o.items || []).reduce(
              (s, i) => s + (i.price || 0) * (i.quantity || 1),
              0
            ),
          0
        ),
        perLocale: tuttiLocali.map((tag) => ({
          tag,
          count: ordiniFiltrati.filter((o) => o.locale === tag).length
        }))
      }
    : null;

  // Raggruppamento ordini per tavolo per la UI
  const groupedByTableUI = {};
  ordiniFiltrati.forEach((o) => {
    const key = String(o.table || "0");
    if (!groupedByTableUI[key]) {
      groupedByTableUI[key] = [];
    }
    groupedByTableUI[key].push(o);
  });

  return (
    <>
      <Navbar />



      <div
        className="page-with-navbar"
        style={{
          padding: "0.8rem",
          maxWidth: "1600px",
          margin: "0 auto",
          boxSizing: "border-box"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem"
          }}
        >
          <h2 style={{ margin: 0 }}>
            {isAdmin
              ? "🛡️ Admin — Tutti gli ordini"
              : tavoloFiltro
              ? `🧾 Ordini Tavolo ${tavoloFiltro}`
              : "🧾 Ordini ricevuti"}
          </h2>

          {tavoloFiltro && (
            <button
              onClick={() => navigate("/ordersdashboard")}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ddd",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "0.9rem",
                color: "#000"
              }}
            >
              ← Tutti i tavoli
            </button>
          )}
        </div>

        {/* Statistiche Admin */}
        {isAdmin && statsAdmin && (
          <div
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              marginBottom: "1.2rem"
            }}
          >
            <div style={statCardStyle("#1976D2")}> 
              <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {statsAdmin.totaleOrdini}
              </span>
              <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>Ordini visibili</span>
            </div>
            <div style={statCardStyle("#388E3C")}>
              <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                €{statsAdmin.totaleIncasso.toFixed(2)}
              </span>
              <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>Incasso totale</span>
            </div>
            {statsAdmin.perLocale.filter((l) => l.count > 0).map((l) => (
              <div key={l.tag} style={statCardStyle("#6D4C41")}>
                <span style={{ fontSize: "1.2rem", fontWeight: 700 }}>{l.count}</span>
                <span style={{ fontSize: "0.75rem", opacity: 0.85 }}>{l.tag}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filtri Admin */}
        {isAdmin && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.7rem",
              marginBottom: "1.2rem",
              padding: "0.9rem 1rem",
              background: "#f0f4ff",
              borderRadius: "10px",
              border: "1px solid #c5cfe8"
            }}
          >
            {/* Filtro Locale */}
            <select
              value={filtroLocale}
              onChange={(e) => setFiltroLocale(e.target.value)}
              style={selectStyle}
            >
              <option value="tutti">🏠 Tutti i locali</option>
              {tuttiLocali.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>

            {/* Filtro Stato */}
            <select
              value={filtroStato}
              onChange={(e) => setFiltroStato(e.target.value)}
              style={selectStyle}
            >
              <option value="tutti">📋 Tutti gli stati</option>
              <option value="in attesa">⏳ In attesa</option>
              <option value="in preparazione">👨‍🍳 In preparazione</option>
              <option value="pronto">✅ Pronto</option>
              <option value="pagamento">💳 Pagamento</option>
              <option value="pagato">💰 Pagato</option>
            </select>

            {/* Ricerca libera */}
            <input
              type="text"
              placeholder="🔍 Cerca tavolo, locale, piatto..."
              value={filtroRicerca}
              onChange={(e) => setFiltroRicerca(e.target.value)}
              style={{
                ...selectStyle,
                minWidth: "220px",
                flex: 1
              }}
            />

            {(filtroLocale !== "tutti" || filtroStato !== "tutti" || filtroRicerca) && (
              <button
                onClick={() => {
                  setFiltroLocale("tutti");
                  setFiltroStato("tutti");
                  setFiltroRicerca("");
                }}
                style={{
                  padding: "0.45rem 1rem",
                  background: "#e53935",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.85rem"
                }}
              >
                ✕ Reset filtri
              </button>
            )}
          </div>
        )}

{/* FILTRI ORDINI */}
<div style={{ marginBottom: "1.5rem" }}>

  <div
    style={{
      display: "flex",
      gap: "0.7rem",
      flexWrap: "wrap"
    }}
  >
    <button
      onClick={() => setFiltroPeriodo("oggi")}
      style={{
        backgroundColor:
          filtroPeriodo === "oggi" ? "#2196F3" : "#3a3a3c",
        color: "white",
        borderRadius: "10px",
        padding: "0.6rem 1.1rem",
        cursor: "pointer",
        border: "none",
        fontWeight: "600"
      }}
    >
      Oggi
    </button>

    <button
      onClick={() => setFiltroPeriodo("tutti")}
      style={{
        backgroundColor:
          filtroPeriodo === "tutti" ? "#2196F3" : "#3a3a3c",
        color: "white",
        borderRadius: "10px",
        padding: "0.6rem 1.1rem",
        cursor: "pointer",
        border: "none",
        fontWeight: "600"
      }}
    >
      Tutti
    </button>
  </div>

  {filtroPeriodo === "tutti" && (
    <div
      style={{
        marginTop: "1rem",
        padding: "1rem",
        borderRadius: "14px",
        background: "#1c1c1e",
        border: "1px solid rgba(255,255,255,0.1)",
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "0.9rem"
      }}
    >

      {/* SALA / ASPORTO */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "0.4rem",
            color: "#aaa",
            fontSize: "0.8rem"
          }}
        >
          Sala / Asporto
        </label>

        <select
          value={filtroTipoOrdine}
          onChange={(e) =>
            setFiltroTipoOrdine(e.target.value)
          }
          style={{
            width: "100%",
            padding: "0.7rem",
            borderRadius: "10px",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#2c2c2e",
            color: "#fff"
          }}
        >
          <option value="tutti">Tutti gli ordini</option>
          <option value="sala">Sala</option>
          <option value="asporto">Asporto</option>
        </select>
      </div>

      {/* STATO */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "0.4rem",
            color: "#aaa",
            fontSize: "0.8rem"
          }}
        >
          Stato
        </label>

        <select
          value={filtroStatoOrdine}
          onChange={(e) =>
            setFiltroStatoOrdine(e.target.value)
          }
          style={{
            width: "100%",
            padding: "0.7rem",
            borderRadius: "10px",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#2c2c2e",
            color: "#fff"
          }}
        >
          <option value="tutti">Tutti gli stati</option>
          <option value="in attesa">In attesa</option>
          <option value="inviato">Inviato</option>
          <option value="in preparazione">
            In preparazione
          </option>
          <option value="pronto">Pronto</option>
          <option value="servito">Servito</option>
          <option value="pagamento">Pagamento</option>
          <option value="pagato">Pagato</option>
        </select>
      </div>

      {/* PERIODO */}
      <div>
        <label
          style={{
            display: "block",
            marginBottom: "0.4rem",
            color: "#aaa",
            fontSize: "0.8rem"
          }}
        >
          Periodo
        </label>

        <select
          value={filtroPeriodoTutti}
          onChange={(e) => {
            setFiltroPeriodoTutti(e.target.value);

            if (e.target.value !== "personalizzato") {
              setDataDa("");
              setDataA("");
            }
          }}
          style={{
            width: "100%",
            padding: "0.7rem",
            borderRadius: "10px",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#2c2c2e",
            color: "#fff"
          }}
        >
          <option value="tutti">Tutto il periodo</option>
          <option value="settimana">Questa settimana</option>
          <option value="mese">Questo mese</option>
          <option value="personalizzato">
            Scegli da - a
          </option>
        </select>
      </div>

      {/* DATE PERSONALIZZATE */}
      {filtroPeriodoTutti === "personalizzato" && (
        <>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.4rem",
                color: "#aaa",
                fontSize: "0.8rem"
              }}
            >
              Da
            </label>

            <input
              type="date"
              value={dataDa}
              onChange={(e) => setDataDa(e.target.value)}
              style={{
                width: "100%",
                padding: "0.7rem",
                borderRadius: "10px",
                border:
                  "1px solid rgba(255,255,255,0.12)",
                background: "#2c2c2e",
                color: "#fff"
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.4rem",
                color: "#aaa",
                fontSize: "0.8rem"
              }}
            >
              A
            </label>

            <input
              type="date"
              value={dataA}
              min={dataDa || undefined}
              onChange={(e) => setDataA(e.target.value)}
              style={{
                width: "100%",
                padding: "0.7rem",
                borderRadius: "10px",
                border:
                  "1px solid rgba(255,255,255,0.12)",
                background: "#2c2c2e",
                color: "#fff"
              }}
            />
          </div>
        </>
      )}

      {/* RESET */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end"
        }}
      >
        <button
          type="button"
          onClick={() => {
            setFiltroTipoOrdine("tutti");
            setFiltroStatoOrdine("tutti");
            setFiltroPeriodoTutti("tutti");
            setDataDa("");
            setDataA("");
          }}
          style={{
            width: "100%",
            padding: "0.7rem",
            borderRadius: "10px",
            border: "none",
            background: "#3a3a3c",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "600"
          }}
        >
          Azzera filtri
        </button>
      </div>

    </div>
  )}
</div>

        {snackbar && <div className="snackbar-modern">{snackbar}</div>}

        {ordineInEliminazione && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "2rem",
                borderRadius: "12px",
                maxWidth: "400px",
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
              }}
            >
              <h3 style={{ marginTop: 0, color: "#d32f2f" }}>
                ⚠️ Conferma eliminazione
              </h3>

              <p style={{ marginBottom: "1.5rem", color: "#555" }}>
                Sei sicuro di voler eliminare l'ordine del tavolo{" "}
                <strong>{ordineInEliminazione.table}</strong>?
                <br />
                Questa azione non può essere annullata.
              </p>

              <div
                style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
              >
                <button
                  onClick={() => setOrdineInEliminazione(null)}
                  style={{
                    padding: "0.6rem 1.5rem",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500"
                  }}
                >
                  Annulla
                </button>

                <button
                  onClick={() => eliminaOrdine(ordineInEliminazione.id)}
                  style={{
                    padding: "0.6rem 1.5rem",
                    backgroundColor: "#d32f2f",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500"
                  }}
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: "#777" }}>🔄 Caricamento ordini...</p>
        ) : ordiniFiltrati.length === 0 ? (
          <p>Nessun ordine disponibile.</p>
        ) : (
          <div className="orders-grid-container">
            {Object.entries(groupedByTableUI).map(([table, ordiniDelTavolo]) => {
  const tuttiItems = ordiniDelTavolo.flatMap((ordine) => {
    const items = ordine.items || ordine.piatti || [];
    return items.map((item, i) => ({
      ...item,
      _ordineId: ordine.id,
      _localIdx: i,
      _ordine: ordine,
      _stato:
        item.stato ||
        ordine.itemStati?.[String(i)] ||
        ordine.stato ||
        "inviato"
    }));
  });

    const statiTutti = tuttiItems.map((it) => it._stato || "inviato");
    let statoCard = "inviato";
    if (statiTutti.includes("pronto")) statoCard = "pronto";
    else if (statiTutti.includes("in preparazione")) statoCard = "in preparazione";
    else if (statiTutti.includes("servito")) statoCard = "servito";

  const totaleComplessivo = ordiniDelTavolo.reduce(
    (sum, o) =>
      sum +
      (o.items || []).reduce(
        (s, i) => s + (i.price || i.prezzo || 0) * (i.quantity || i.quantita || 1),
        0
      ),
    0
  );

  const groupedItems = {};

  tuttiItems.forEach((item) => {
    const nome = item.name || item.nome || "";
    const stato = item._stato || "inviato";
    const key = `${nome}_${item.custom || ""}_${stato}`;

    if (!groupedItems[key]) {
      groupedItems[key] = {
        ...item,
        name: nome,
        quantity: 0
      };
    }

    groupedItems[key].quantity += item.quantity || item.quantita || 1;
  });

  const primoOrdine = ordiniDelTavolo[0];

  return (
    <div
      key={table}
      className={`ordine-card stato-${statoCard.replace(/\s/g, "-")}`}
    >
      <div className="ordine-header">
        <div className="ordine-top-row">
          <div className="ordine-numero">
            {String(table).toUpperCase().startsWith("ASPORTO")
              ? `🥡 ${table}`
              : `🪑 Tav. ${table}`}
          </div>

          <div className={`ordine-stato stato-${statoCard.replace(/\s/g, "-")}`}>
            {statoCard}
          </div>
        </div>

        {isAdmin && primoOrdine?.locale && (
          <div
            style={{
              display: "inline-block",
              marginTop: "0.3rem",
              padding: "0.15rem 0.55rem",
              background: "#1976D2",
              color: "white",
              borderRadius: "6px",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.3px"
            }}
          >
            🏠 {primoOrdine.locale}
          </div>
        )}

        <div className="ordine-info">
          {primoOrdine?.telefonoCliente && <>📞 {primoOrdine.telefonoCliente} • </>}
          {primoOrdine?.cameriere_id === "GESTORE" && <>👑 Gestore • </>}
          {primoOrdine?.cameriere_id !== "GESTORE" &&
            primoOrdine?.cameriere_id &&
            camerieriMap[primoOrdine.cameriere_id] && (
              <>👨‍🍳 {camerieriMap[primoOrdine.cameriere_id]} • </>
            )}
          ⏱️{" "}
          {primoOrdine?.timestamp?.seconds
            ? new Date(primoOrdine.timestamp.seconds * 1000).toLocaleTimeString()
            : "—"}
        </div>

        <div className="ordine-info" style={{ marginTop: "4px", fontWeight: 700 }}>
          Totale: €{totaleComplessivo.toFixed(2)}
        </div>
      </div>

      <div className="ordine-body">
        {Object.values(groupedItems).map((item, i) => {
          const categoria = getCategoriaByName(item.name);
          const hue = getCategoryHue(categoria);
          const primary = `hsl(${hue}, 65%, 45%)`;

          return (
            <div key={i} className="item-row">
              <div
                className="category-stripe"
                style={{ backgroundColor: primary }}
              />
              <div className="item-text">
                {item.quantity} × {item.name}

                <div
                  className="ordine-info"
                  style={{
                    fontSize: "0.75rem",
                    marginTop: "2px",
                    color:
                      item._stato === "pronto"
                        ? "#4CAF50"
                        : item._stato === "in preparazione"
                        ? "#FF9800"
                        : item._stato === "servito"
                        ? "#9E9E9E"
                        : "#888"
                  }}
                >
                  ● {item._stato}
                </div>

                {item.custom && (
                  <div className="ordine-info">{item.custom}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="azioni-ordine">
        <button
          className="btn-dettagli"
          onClick={() =>
            setOrdineSelezionato({
              table: table,
              locale: primoOrdine.locale,
              ordiniOriginali: ordiniDelTavolo
            })
          }
        >
          ✏️ Modifica
        </button>

        <button
          className="btn-elimina"
          onClick={() =>
            setOrdineInEliminazione({
              ...primoOrdine,
              ordiniIds: ordiniDelTavolo.map((o) => o.id)
            })
          }
        >
          🗑️ Elimina
        </button>
      </div>
    </div>
  );
})}
          </div>
        )}

        {ordineSelezionato && (
          <div className="cameriere-riepilogo-overlay">
            <MenuEditorOverlay
              ordine={ordineSelezionato}
              localeTag={ordineSelezionato?.locale || localeTag}
              onClose={() => setOrdineSelezionato(null)}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default OrdersDashboard;